import React, { useState, useEffect } from 'react';
import { surveyService, SectionDetail } from '../../services/surveyService';

interface Props {
  gisId: string;
  onClose: () => void;
}

const SectionDetailModal: React.FC<Props> = ({ gisId, onClose }) => {
  const [detail, setDetail] = useState<SectionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        setLoading(true);
        const data = await surveyService.getSectionDetail(gisId);
        setDetail(data);
      } catch (err) {
        setError('Failed to load section detail');
        console.error('Error loading section detail:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [gisId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {detail ? detail.sectionName : `Section ${gisId}`}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          )}
          
          {error && (
            <div className="text-red-600 text-center py-8">{error}</div>
          )}
          
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Survey Summary</h3>
                  <div className="space-y-1 text-sm">
                    <p>Responses: {detail.summary.responseCount}</p>
                    <p>Average Rating: {detail.summary.averageRating?.toFixed(1) || 'N/A'}</p>
                    <p>Skip Rate: {detail.summary.skipRate.toFixed(1)}%</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Time Analysis</h3>
                  <div className="space-y-1 text-sm">
                    <p>Sample Size: {detail.timeAnalysis.sampleSize}</p>
                    <p>Mean Time: {detail.timeAnalysis.meanActualTime?.toFixed(1) || 'N/A'} hrs</p>
                    <p>Median Time: {detail.timeAnalysis.medianActualTime?.toFixed(1) || 'N/A'} hrs</p>
                  </div>
                </div>
              </div>
              
              {detail.userComments.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Recent Comments</h3>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {detail.userComments.slice(0, 5).map((comment, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-800">{comment.comment}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {comment.userName || 'Anonymous'} - {new Date(comment.date).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="border-t p-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionDetailModal;